import styled, { css, useTheme } from "styled-components";
import AntModal, { ModalProps as AntModalProps } from "antd/es/modal";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice";
import { buttonStyles } from "common/Buttons/Button";
import { hexToRGB } from "@shared/utils/misc";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";

export const modalStyles = css`
  .ant-modal-mask {
    background: ${(p) => hexToRGB(p.theme.colors.black, 0.6)};
  }
  .ant-modal-container,
  .ant-modal-header {
    background: ${(p) => p.theme.colors.cardBgColor};
  }
  .ant-modal-title {
    color: ${(p) => p.theme.colors.titleColor};
  }
  .ant-modal-close-icon {
    color: ${(p) => p.theme.colors.textColor};
  }
  .ant-modal {
    .ant-btn {
      ${buttonStyles.antBtn};
    }
    .ant-btn-primary {
      ${buttonStyles.antBtnPrimary};
    }
    .ant-btn-dangerous {
      ${buttonStyles.antBtnDangerous};
    }
    .ant-btn[disabled] {
      ${buttonStyles.antBtnDisabled};
    }
    .ant-btn-default {
      ${buttonStyles.antBtnDefault};
    }
    .ant-modal-confirm-content,
    .ant-modal-confirm-title {
      color: ${(p) => p.theme.colors.primaryText};
    }
  }
  .ant-modal-container {
    padding: 0;
  }
  .ant-modal-body {
    padding: 24px;
  }
  .ant-modal-header {
    margin-bottom: 0;
    padding: 16px 24px;
    border-bottom: ${(p) => p.theme.borders.lightest};
  }
  .ant-modal-footer {
    margin-top: 0;
    padding: 10px 16px;
    border-top: ${(p) => p.theme.borders.lightest};
  }
  .ant-modal-confirm.ant-modal .ant-modal-body {
    padding: 24px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      .ant-modal-mask {
        background: ${hexToRGB("#0B0D12", p.theme.isDarkMode ? 0.6 : 0.36)};
      }
      .ant-modal-container {
        border-radius: ${p.theme.radii.card}px;
        overflow: hidden;
      }
      .ant-modal-header {
        padding: 20px 56px 4px 24px;
        border-bottom: none;
      }
      /* Title on the left in the panel-title ink, 16/600; no rule under it. */
      .ant-modal-title {
        font-size: 16px;
        line-height: 24px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
        letter-spacing: -0.005em;
        text-align: start;
      }
      /* Body copy in the body role. */
      .ant-modal-body {
        font-size: 14px;
        line-height: 20px;
        color: ${p.theme.colors.primaryText};
      }
      .ant-modal-header + .ant-modal-body {
        padding-top: 12px;
      }
      /* Actions sit on the right (antd aligns them to the end, 8px apart);
         no display rule here, so modals that hide their footer keep it
         hidden. */
      .ant-modal-footer {
        padding: 0 24px 20px;
        border-top: none;
      }
      .ant-modal-body:has(+ .ant-modal-footer) {
        padding-bottom: 20px;
      }
      /* Confirm dialogs: the same title and body roles, actions right. */
      .ant-modal-confirm {
        .ant-modal-confirm-title {
          color: ${p.theme.colors.titleColor};
          font-size: 16px;
          line-height: 24px;
          font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
        }
        .ant-modal-confirm-content {
          margin-top: 6px;
          font-size: 14px;
          line-height: 20px;
          color: ${p.theme.colors.primaryText};
        }
        .ant-modal-confirm-btns {
          margin-top: 20px;
        }
      }
      .ant-modal-close {
        top: 16px;
        inset-inline-end: 16px;
        border-radius: 10px;
      }
      .ant-modal-close-icon {
        color: ${p.theme.colors.secondaryText};
      }
      /* antd focuses the close button on open; only keyboard focus shows a ring. */
      .ant-modal-close:focus:not(:focus-visible) {
        outline: none;
        box-shadow: none;
      }
    `}
`;

const StyledModal = styled(AntModal)`
  ${modalStyles};
  ${(p) =>
    !p.footer &&
    !p.onOk &&
    `
   .ant-modal-footer {
      display: none;
    }
  `};
`;

interface ModalProps extends AntModalProps {
  onCancel?: () => void;
}

const Modal = ({ open, onCancel, mask, ...props }: ModalProps) => {
  const { registerBackButtonListener } = useBackButtonHandler();

  useEffect(() => {
    if (open) {
      return registerBackButtonListener(() => {
        onCancel?.();
        return true;
      });
    }
  }, [open]);

  return (
    <StyledModal
      open={open}
      onCancel={onCancel}
      mask={{ enabled: true, blur: false }}
      {...props}
    />
  );
};

Modal.useModal = () => {
  const [modal, warningModalContextHolder] = AntModal.useModal();
  const [open, setOpen] = useState(false);
  const isDarkMode = useSelector(selectIsDarkMode);
  const { isNewLook } = useTheme();
  const modalPropsRef = useRef<{ destroy: () => void }>(undefined);
  const { registerBackButtonListener } = useBackButtonHandler();

  const methodToReplace = [
    "info",
    "success",
    "error",
    "warning",
    "confirm",
  ] as const;
  const originalMethods = useRef<{
    [key in (typeof methodToReplace)[number]]?: (p: any) => void;
  }>({});

  useEffect(() => {
    if (open) {
      return registerBackButtonListener(() => {
        modalPropsRef.current?.destroy?.();
        return true;
      });
    }
  }, [open]);

  useEffect(() => {
    for (let method of methodToReplace) {
      if (!(method in originalMethods.current)) {
        originalMethods.current[method] = modal[method];
      }
      // @ts-ignore
      modal[method] = (props) => {
        // @ts-ignore
        modalPropsRef.current = originalMethods.current?.[method]?.({
          ...props,
          className: isDarkMode
            ? `${props.className} dark-mode`
            : props.className,
          // New look: confirm dialogs dim the page like every other modal
          // instead of blurring it.
          ...(isNewLook && { mask: { enabled: true, blur: false } }),
          afterOpenChange: (open: boolean) => {
            props.afterOpenChange?.(open);
            setOpen(open);
          },
        });
      };
    }
  }, [isDarkMode, isNewLook]);

  return [modal as any, warningModalContextHolder];
};

export default Modal;
